import { attributeTypes } from 'utils';
import { selectTypeEnums } from './attributes/constnats';

/**
 * "Minimum selections" for multi-choice inputs: CHECK_BOX and multi SELECT
 * fields (`properties.minSelections`) and Checkbox group / Multi select table
 * cells (`tableHead[i].minSelections`, per column). A whole number; absent / 0
 * means no minimum. It is capped at the number of options, so a template whose
 * author set a minimum above the option count can still be submitted.
 *
 *   Required    — 0 selected is an error; fewer than the minimum is an error.
 *   Not required — 0 selected is fine (the question may be skipped); once the
 *                  user starts selecting, fewer than the minimum is an error.
 */

export const SELECTION_ERRORS = { REQUIRED: 'REQUIRED', MIN: 'MIN' };

/** A configured minimum, sanitised to a whole number ≥ 0. */
export const toMinSelections = (raw) => {
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
};

/** The minimum actually enforced: the configured one, capped at the option count. */
export const getEffectiveMinSelections = (rawMin, optionCount) => {
    const min = toMinSelections(rawMin);
    return optionCount ? Math.min(min, optionCount) : min;
};

export const countSelections = (value) => (Array.isArray(value) ? value.filter(Boolean).length : 0);

/** `null` when `count` is acceptable, else a `SELECTION_ERRORS` value. */
export const checkSelectionCount = (count, { required, min }) => {
    if (count === 0) return required ? SELECTION_ERRORS.REQUIRED : null;
    return min && count < min ? SELECTION_ERRORS.MIN : null;
};

export const isMultiChoiceAttribute = (item) =>
    item?.type === attributeTypes.CHECK_BOX || (item?.type === attributeTypes.SELECT && item?.defaultDateType === selectTypeEnums.MULTIPLE);

/** The configured minimum of a field. */
export const getConfiguredMinSelections = (item) => toMinSelections(item?.properties?.minSelections);

const getFieldMin = (item) =>
    isMultiChoiceAttribute(item) ? getEffectiveMinSelections(item?.properties?.minSelections, item?.properties?.options?.length || 0) : 0;

/**
 * react-hook-form rules for a multi-choice field. `required` is the field's
 * effective Required (after Conditional Logic), `label` its display name.
 */
export const buildSelectionRules = (item, required, label) => {
    const min = getFieldMin(item);
    if (!required && !min) return {};
    return {
        validate: (value) => {
            const count = countSelections(value);
            const error = checkSelectionCount(count, { required, min });
            if (!error) return true;
            if (error === SELECTION_ERRORS.REQUIRED) {
                return min > 1 ? `Select at least ${min} options${label ? ` for ${label}` : ''}.` : `${label || 'This field'} is required.`;
            }
            return `Select at least ${min} options${label ? ` for ${label}` : ''} (${count} selected).`;
        },
    };
};

/** Label marker: `*` only when the field is required — a minimum alone does not make it required. */
export const selectionRequiredMark = (item, required) => (required ? '*' : '');
